import { BOSSES, type BossType, type NightmareType } from './nightmares.ts';
import { sec } from './units.ts';

/**
 * budget = round(base × mareaBp[step] × activity^0.75 / sectors) × the scales — SPEC §8.2. A bastion wave's step comes
 * from the heroes' level (campaign/level.ts) and its ramp is the campaign's pressure (PRESSURE); `waveStepBp` is the
 * calendar's ramp, kept only for the side sagas' bell nights and the pressure's start.
 */
export const BUDGET = {
  base: 170,
  /**
   * Each Marea's first wave, against the first Marea's. A wave 6 is ×1.6 its Marea's first, so each
   * Marea opens a little above the last one's end (M2: 1.7 after 1.6) and later ones ease back:
   * the old flat +115 % per Marea made the Wardens' first wave of Marea II +34 % over their last one.
   */
  mareaBp: [10000, 17000, 27500, 40000, 53000, 67500],
  waveStepBp: 1200,
  /** After a lost Marea, next Marea gets +20 %. */
  lossPenaltyBp: 2000,
};

export interface MareaDef {
  weights: Partial<Record<NightmareType, number>>;
  /** Chance of grade 2 (ink) per spawned nightmare, deep sleep only. */
  inkBp: number;
}

/** Era I's new creatures (docs/CRIATURAS.md) join from the second Marea: slingers and crows first, the mourner and the ogre a Marea later. */
export const MAREAS: MareaDef[] = [
  { weights: { goblin: 6, wolf: 3 }, inkBp: 0 },
  { weights: { goblin: 5, wolf: 3, skeleton: 2, slinger: 2, crow: 2 }, inkBp: 1500 },
  { weights: { goblin: 4, wolf: 2, skeleton: 2, wraith: 2, slinger: 2, crow: 2, mourner: 1, ogre: 1 }, inkBp: 2500 },
  { weights: { goblin: 3, wolf: 2, skeleton: 2, wraith: 2, gargoyle: 1, slime: 1, slinger: 2, crow: 2, mourner: 1, ogre: 1 }, inkBp: 3500 },
  { weights: { goblin: 3, wolf: 2, skeleton: 2, wraith: 2, gargoyle: 1, slime: 1, stalker: 2, slinger: 2, crow: 2, mourner: 1, ogre: 1 }, inkBp: 4500 },
  { weights: { goblin: 3, wolf: 2, skeleton: 2, wraith: 2, gargoyle: 1, slime: 1, stalker: 2, slinger: 2, crow: 2, mourner: 1, ogre: 1 }, inkBp: 5500 },
];

/**
 * War hosts (docs/CRIATURAS.md §1): each bastion wave draws one, and it reshapes the table so no two waves
 * look alike. `mult` scales the weight of those kinds (bp), `rest` every other kind; the points stay the
 * same, so a host of heavy kinds brings fewer bodies. A host only comes from Marea `from` (index) and when
 * at least one of its kinds is in the wave's table. `budgetBp` evens it out: points don't price bodies
 * well (a horde of cheap ones is far harder than a siege of the same points), so each host spends its
 * share of the budget. The omen names it ahead.
 */
export type HostId = 'common' | 'horde' | 'flock' | 'siege' | 'procession' | 'ambush' | 'giantStep';
export interface HostDef {
  from: number;
  weight: number;
  mult: Partial<Record<NightmareType, number>>;
  rest: number;
  budgetBp: number;
}
export const HOSTS: Record<HostId, HostDef> = {
  common: { from: 0, weight: 3, mult: {}, rest: 10000, budgetBp: 10000 },
  horde: { from: 1, weight: 2, mult: { goblin: 25000, wolf: 20000, crow: 25000, troll: 20000, spiderling: 20000 }, rest: 2500, budgetBp: 8000 },
  flock: { from: 1, weight: 2, mult: { crow: 60000, wraith: 40000, gargoyle: 40000, wyvern: 40000, moth: 60000, wyrmling: 40000 }, rest: 1500, budgetBp: 7500 },
  siege: { from: 2, weight: 2, mult: { giant: 30000, ogre: 50000, ram: 40000, mammoth: 40000, beetle: 40000, archer: 30000, slinger: 30000, sapper: 20000, skeleton: 15000 }, rest: 2500, budgetBp: 9500 },
  procession: { from: 2, weight: 2, mult: { oracle: 40000, mourner: 50000, bonesinger: 40000, bannerman: 40000, skeleton: 20000, slime: 15000 }, rest: 6000, budgetBp: 9500 },
  // The giant's pass (era III): one or two giants with a small escort, the whole party on one.
  giantStep: { from: 2, weight: 1, mult: { giant: 400000 }, rest: 1200, budgetBp: 9500 },
  ambush: { from: 4, weight: 1, mult: { weaver: 40000, stalker: 40000, sapper: 30000, wolf: 20000 }, rest: 4000, budgetBp: 8500 },
};
export const HOST_IDS = Object.keys(HOSTS) as HostId[];

export const WAVE = {
  /** Spawns are staggered over the first 90 s (a wave under a battle plan, or a practice one; the rest come in `stages`). */
  spawnWindow: sec(90),
  /**
   * A bastion wave without a plan swells as it comes, with no lull: each stage takes `shareBp` of a sector's
   * groups, starting `at` after the lead-in and spread over `len`; the warlord comes with the last. Added on
   * 2026-10-28: the wave arrived evenly over 90 s and fell as fast as it came (a version with lulls between
   * pushes was dropped: they would be waits, not breathers).
   */
  stages: [
    { shareBp: 2000, at: sec(0), len: sec(40) },
    { shareBp: 3500, at: sec(40), len: sec(40) },
    { shareBp: 4500, at: sec(80), len: sec(40) },
  ] as { shareBp: number; at: number; len: number }[],
  groupMin: 3,
  groupMax: 6,
  /** Hard cap: 5 minutes of play. */
  maxTicks: sec(300),
  /** At the cap, each surviving nightmare hits the keep this many times. */
  capHits: 10,
  /** Ticks of calm before the first spawn. */
  leadIn: sec(5),
  /** Share of a bastion wave's groups that come across the rough land from any side, not by a path. */
  wildBp: 3000,
  /**
   * Share of a bastion wave's path groups that leave the path near the wall and storm it 2 to 5 cells to
   * one side of their gate (2026-10-25: everything followed the path to the gate, one pile per gate).
   * Only from Marea `flankFromMarea` (0-based) on: a breach draws the whole wave and ghosts don't react,
   * and losing the third Marea ends a campaign (see DECISIONS).
   */
  flankBp: 2500,
  flankFromMarea: 3,
  flankMin: 2,
  flankMax: 5,
  /** How far out from the wall (cells) a flanking group leaves its path. */
  flankFrom: 5,
  /** Most groups a sector draws: past it, a wave grows stronger instead of bigger (bodies a phone can draw). */
  maxGroups: 200,
  /**
   * What a sector has left past `maxGroups` inks its nightmares instead (grade 2): each costs this share (×1.4)
   * of its points. Added on 2026-10-25, when the Wardens' Marea III hit the cap from wave 2 on.
   */
  overflowInkCostBp: 14000,
  /**
   * Once every nightmare of a sector is inked, what is still left makes them stronger: health and damage
   * each × √(1 + left / (spent × this)). Added on 2026-10-28, when the Wardens' Marea VI threw away two
   * thirds of its budget at the cap (a bigger budget drew the very same wave).
   */
  overflowStrengthCostBp: 15000,
  /**
   * A campaign without a challenge: the budget grows this much per mean hero level above 1 (a harder campaign has its own,
   * CHALLENGE.hard.perLevelBp). Added with the class balance (docs/BALANCE_CLASES.md): skills grow with the level, and 6 %
   * keeps campaign-4p at least as hard as before (bots 6.7 % against 9.5 %).
   */
  perLevelBp: 600,
  /** Each hero played live adds this much to the wave's budget: people play far better than their ghosts. */
  liveBp: 1000,
  /**
   * The mean hero level that brings each Marea's creatures (table, hosts, plans, ink, warlord) whatever the
   * calendar says: a group that levelled in crypts before its first wave meets what its level calls for.
   * Anchored on the cellars' «level 8 = Marea III». Added on 2026-11-03, after a group of seven at level 7
   * won its first wave against goblins and wolves without a scratch (docs/DECISIONS.md).
   */
  levelMarea: [1, 4, 8, 11, 14, 17] as number[],
  /** Past the last Marea of `levelMarea`, each Marea expects this many levels more. */
  levelMareaStep: 3,
  /**
   * Each mean hero level past the one its step expects (`levelMarea`) adds this much to the health and
   * the damage of every nightmare of the wave (not the boss). More of the same nightmares didn't make a wave any
   * harder for a group levels ahead: they died before they reached anyone (2026-11-03, docs/DECISIONS.md).
   * 11000 until the same day's evening: Khorvaire won its next wave at ×8 without a leak, and the simulations had
   * kept underestimating the people, so it went up on purpose past what they called comfortable.
   */
  levelStrengthBp: 20000,
  /**
   * Each mean hero level past the one its step expects adds this much to the wave's budget (more of
   * them, on top of `perLevelBp`). Added on 2026-11-03 with the raise above: a group levels ahead met a wave that
   * looked small for it (docs/DECISIONS.md). Mind the phones: the wave's size is also performance (SPEC §12).
   */
  levelBudgetBp: 1500,
};

/**
 * A harder pace a campaign can opt into ("challenge": "hard"): the budget follows the group's
 * heroes and what it has built, wall-breakers come from the first Marea, and every wave has a warlord.
 * budget × budgetBp × (1 + perLevelBp × (mean level − 1)) × (1 + perBuildBp × structure levels, capped).
 * Structure levels leave out the starting ring. Tuned on 2026-10-24 against the Wardens' real bastion
 * (58 levels after four waves, all won untouched): it now draws blood without breaking the keep.
 */
export const CHALLENGE = {
  hard: {
    budgetBp: 15000,
    /** 12 % until the class balance (docs/BALANCE_CLASES.md): skills grow with the level, the warrior cleaves; 28 % keeps the Wardens' waves at least as hard as before (balance:probe: buildings lost 22 against 17, the keep the same). */
    perLevelBp: 2800,
    /** Every level built adds this much (to this much at most): building has to keep paying off (2026-10-25: was 380 up to 30000). */
    perBuildBp: 150,
    maxBuildBp: 10000,
    /** Added to each Marea's table: gargoyles hunt buildings, skeletons and slimes batter walls and gates (gargoyles 1, was 3). */
    extraWeights: { gargoyle: 1, skeleton: 3, slime: 2 } as Partial<Record<NightmareType, number>>,
    eliteFromMarea: 0,
  },
};
export type Challenge = keyof typeof CHALLENGE;

/**
 * The first season's Mareas: winning the sixth (the Hollow Shell) wins the season, and the campaign goes
 * on with the late Mareas (docs/PROGRESION.md).
 */
export const MAREAS_PER_CAMPAIGN = 6;
export const WAVES_PER_MAREA = 6;

/**
 * Late Mareas (VII on, docs/PROGRESION.md): the tide never ends.
 * - `budgetStepBp`: each one's first wave adds this much to BUDGET.mareaBp past its end (the curve's own last step);
 * - `inkStepBp`/`inkMaxBp`: the deep sleep's ink keeps rising past the last Marea's, up to the cap;
 * - a boss returns every `bossEvery` Mareas from `bossFrom` (index) on its last wave, the bosses taking turns
 *   (`bossOrder`), its health grown with the Marea's budget against its own Marea's (× `bossHpBp`);
 * - `xpStepBp`: every experience a hero earns is worth this much more per Marea past the first season;
 * - `repeatFrom` (index): from this Marea on, a lost Marea is played again (no +20 %), and the campaign never ends;
 * - `stir`: every second loss in a row there the titan stirs: everything built drops to `structureHpBp` of its
 *   life and the storage loses `storageBp` (on top of the quarter a Great Work under way loses).
 */
export const LATE_MAREAS = {
  budgetStepBp: 14500,
  inkStepBp: 500,
  inkMaxBp: 8000,
  bossFrom: 8,
  bossEvery: 3,
  bossOrder: ['witch', 'shell'] as BossType[],
  bossHpBp: 10000,
  xpStepBp: 1000,
  repeatFrom: 5,
  stir: { structureHpBp: 5000, storageBp: 2500 },
};

/**
 * The pressure (docs/SPEC.md §8.2, «Presión»): each campaign's own scale on its bastion waves' budget and boss,
 * set by what its real waves did, one scale for the waves played live and one for those its ghosts fight alone.
 * No simulation ever predicted how well people defend (2026-11-03: the Wardens won ~40 live waves in a row with
 * the keep untouched; Khorvaire, two), so the waves stop guessing: after each one the scale moves by `steps`, the
 * first whose `keepBp` (the keep's life the wave took, bp) is not reached, until the waves hurt about as much as
 * `aim` says. A lost wave takes `lostBp`. Bounds: `minBp` .. `maxBp`. Past the cap on groups the extra points
 * ink and then strengthen the nightmares (WAVE.maxGroups), so the count stays within what the phones carry.
 */
export const PRESSURE = {
  /**
   * The keep's life a wave should take, in bp (what the steps aim at; for the docs and the tools). Low, because
   * the keep carries its wounds through the Marea's six waves: 3 to 12 % a wave leaves it standing, but worn.
   */
  aim: [300, 1200] as [number, number],
  steps: [
    { keepBp: 100, stepBp: 12000 },
    { keepBp: 300, stepBp: 10800 },
    { keepBp: 1200, stepBp: 10000 },
    { keepBp: 2500, stepBp: 9300 },
    { keepBp: 10001, stepBp: 8500 },
  ] as { keepBp: number; stepBp: number }[],
  lostBp: 7500,
  minBp: 5000,
  maxBp: 1_000_000,
};

/** A Marea's first-wave budget against the first Marea's (bp): the table, then LATE_MAREAS.budgetStepBp each. */
export const mareaBudgetBp = (m: number): number => {
  const last = BUDGET.mareaBp.length - 1;
  if (m <= last) return BUDGET.mareaBp[m < 0 ? 0 : m] as number;
  return (BUDGET.mareaBp[last] as number) + (m - last) * LATE_MAREAS.budgetStepBp;
};

/** A Marea's chance of ink per nightmare in the deep sleep (bp). */
export const mareaInkBp = (m: number): number => {
  const last = MAREAS.length - 1;
  const base = (MAREAS[m < last ? (m < 0 ? 0 : m) : last] as MareaDef).inkBp;
  if (m <= last) return base;
  const v = base + (m - last) * LATE_MAREAS.inkStepBp;
  return v < LATE_MAREAS.inkMaxBp ? v : LATE_MAREAS.inkMaxBp;
};

/** Experience is worth this much (bp) in Marea `m`. */
export const mareaXpBp = (m: number): number => 10000 + (m >= MAREAS_PER_CAMPAIGN ? (m - MAREAS_PER_CAMPAIGN + 1) * LATE_MAREAS.xpStepBp : 0);

/** The boss of a Marea's wave, if any: its own (BOSSES), or a returning one in the late Mareas. */
export const bossAt = (m: number, w: number): BossType | null => {
  for (const b of Object.values(BOSSES)) if (b.marea === m && b.wave === w) return b.type;
  const L = LATE_MAREAS;
  if (m < L.bossFrom || w !== WAVES_PER_MAREA - 1 || (m - L.bossFrom) % L.bossEvery !== 0) return null;
  return L.bossOrder[((m - L.bossFrom) / L.bossEvery) % L.bossOrder.length] ?? null;
};

/** A boss's health in Marea `m` before the activity scale: its own, grown with the budget past its own Marea. */
export const bossBaseHp = (type: BossType, m: number): number => {
  const b = BOSSES[type];
  if (m <= b.marea) return b.hpBase;
  return Math.trunc((Math.trunc((b.hpBase * mareaBudgetBp(m)) / mareaBudgetBp(b.marea)) * LATE_MAREAS.bossHpBp) / 10000);
};
